import supabase from '../supabaseClient';

/**
 * Join the iOS app waitlist
 * @param {Object} data - Waitlist signup data
 * @param {string} data.email - User's email (required)
 * @param {string} data.name - User's name (optional)
 * @param {string} data.phone - User's phone (optional)
 * @param {string} data.referralSource - How they heard about us (optional)
 * @returns {Promise<Object>} The created waitlist entry
 */
export async function joinWaitlist({ email, name, phone, referralSource }) {
  if (!email) {
    throw new Error('Email is required');
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new Error('Please enter a valid email address');
  }

  const { data, error } = await supabase
    .from('ios_waitlist')
    .insert([
      {
        email: email.toLowerCase().trim(),
        name: name?.trim() || null,
        phone: phone?.trim() || null,
        referral_source: referralSource || null
      }
    ])
    .select()
    .single();

  if (error) {
    // Check for unique constraint violation (duplicate email)
    if (error.code === '23505') {
      throw new Error('This email is already on the waitlist');
    }
    throw new Error(error.message);
  }

  return data;
}

/**
 * Check if an email is already on the waitlist
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} True if email exists in waitlist
 */
export async function checkWaitlistStatus(email) {
  if (!email) {
    return false;
  }

  const { data, error } = await supabase
    .from('ios_waitlist')
    .select('id, status, created_at')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();

  if (error) {
    console.error('Error checking waitlist status:', error);
    return false;
  }

  return !!data;
}

/**
 * Get waitlist entry for the current authenticated user
 * @returns {Promise<Object|null>} Waitlist entry or null
 */
export async function getMyWaitlistEntry() {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user?.email) {
    return null;
  }

  const { data, error } = await supabase
    .from('ios_waitlist')
    .select('*')
    .eq('email', user.email.toLowerCase())
    .maybeSingle();

  if (error) {
    console.error('Error fetching waitlist entry:', error);
    return null;
  }

  return data;
}
